<script setup lang="ts">
import type { DesignModel } from '@forgd/contract/types'
import { useNumberFieldBlurGuard } from '#design-tokenomics/composables/useNumberFieldBlurGuard'
import { formatValue } from '@forgd/formatter'
import { Field, FieldContent, FieldLabel } from '@forgd/ui-kit/blocks/field'
import { Card, CardContent } from '@forgd/ui-kit/card'
import { Input } from '@forgd/ui-kit/input'
import { InputGroup } from '@forgd/ui-kit/input-group'
import { NumberField, NumberFieldInput } from '@forgd/ui-kit/number-field'

const model = defineModel<DesignModel>({ required: true })

const id = useId()

const multiplierFormat = { maximumFractionDigits: 0 }
const multiplierGuard = useNumberFieldBlurGuard(multiplierFormat)

const multiplier = computed({
  get: () => model.value.AIC,
  set: (value: number) => {
    model.value = {
      ...model.value,
      AIC: value,
      postTgePop: { ...model.value.postTgePop, postTgePopMultiplier: value },
    }
  },
})

function updateMultiplier(value: number | undefined) {
  if (multiplierGuard.accepts(value, multiplier.value)) {
    multiplier.value = value !== undefined && Number.isFinite(value) ? value : 1
  }
}

const priceAtTge = computed(() => (model.value.MTS > 0 ? model.value.FDV / model.value.MTS : 0))
const poppedPrice = computed(() => priceAtTge.value * multiplier.value)
const priceChange = computed(() =>
  priceAtTge.value > 0 ? ((poppedPrice.value - priceAtTge.value) / priceAtTge.value) * 100 : 0,
)
</script>

<template>
  <div class="space-y-6">
    <Card>
      <CardContent class="space-y-5">
        <Field orientation="horizontal">
          <FieldLabel :for="`${id}-listing-price`">
            Initial Listing Token Price
          </FieldLabel>
          <FieldContent>
            <Input :id="`${id}-listing-price`" :model-value="formatValue(priceAtTge, 'currency')" disabled />
          </FieldContent>
        </Field>
        <Field orientation="horizontal">
          <FieldLabel :for="`${id}-multiplier`">
            Post-TGE 'Pop' Multiplier
          </FieldLabel>
          <FieldContent>
            <NumberField
              :model-value="multiplier"
              :min="1"
              :format-options="multiplierFormat"
              :step-snapping="false"
              @update:model-value="updateMultiplier"
            >
              <InputGroup>
                <NumberFieldInput
                  v-bind="multiplierGuard.inputListeners"
                  :id="`${id}-multiplier`"
                  data-testid="dt-adjust-post-tge-pop"
                />
              </InputGroup>
            </NumberField>
          </FieldContent>
        </Field>
        <Field orientation="horizontal">
          <FieldLabel :for="`${id}-popped-price`">
            Resulting Post-TGE 'Pop' Price
          </FieldLabel>
          <FieldContent>
            <Input :id="`${id}-popped-price`" :model-value="formatValue(poppedPrice, 'currency')" disabled />
          </FieldContent>
        </Field>
        <Field orientation="horizontal">
          <FieldLabel :for="`${id}-price-change`">
            Price Change
          </FieldLabel>
          <FieldContent>
            <Input :id="`${id}-price-change`" :model-value="formatValue(priceChange, 'percent')" disabled />
          </FieldContent>
        </Field>
      </CardContent>
    </Card>
  </div>
</template>
